import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { fromTaipeiWall, toTaipeiWall } from '../../lib/schedule';

interface Props {
  value: number | null;
  onChange: (ms: number | null) => void;
}

/**
 * DatePicker 用的是瀏覽器本地時區；為了讓「台北時間」在任何時區的電腦都正確，
 * 這裡把台北牆上時間塞進一個本地 Dayjs 只做「顯示」，選完再讀回欄位轉成時間戳。
 */
function wallDayjs(ms: number): Dayjs {
  const w = toTaipeiWall(ms);
  return dayjs(new Date(w.year, w.month, w.day, w.hour, w.minute));
}

function dayjsToMs(d: Dayjs): number {
  return fromTaipeiWall({ year: d.year(), month: d.month(), day: d.date(), hour: d.hour(), minute: d.minute() });
}

const range = (from: number, to: number) =>
  Array.from({ length: Math.max(0, to - from) }, (_, i) => from + i);

/** 過去的日期（以台北日期判斷）不可選。 */
const disabledDate = (d: Dayjs) => d.isBefore(wallDayjs(Date.now()), 'day');

/** 台北的今天：已過去的小時 / 分鐘（含當下這一分鐘）不可選。 */
function disabledTime(d: Dayjs | null) {
  const now = wallDayjs(Date.now());
  if (!d || !d.isSame(now, 'day')) return {};
  return {
    disabledHours: () => range(0, now.hour()),
    disabledMinutes: (h: number) => {
      if (h < now.hour()) return range(0, 60);
      return h === now.hour() ? range(0, now.minute() + 1) : [];
    },
  };
}

/** 排程刊登時間：台北時間、只到分鐘（無秒數欄），不可選過去時間，回傳秒數歸零的毫秒時間戳。 */
export default function ScheduleTimePicker({ value, onChange }: Props) {
  return (
    <DatePicker
      className="w-full"
      showTime={{ format: 'HH:mm' }}
      format="YYYY-MM-DD HH:mm"
      showNow={false}
      placeholder="選擇刊登時間（台北時間）"
      disabledDate={disabledDate}
      disabledTime={disabledTime}
      value={value ? wallDayjs(value) : null}
      onChange={(d) => onChange(d ? dayjsToMs(d) : null)}
    />
  );
}
